import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  formatListeningCreatedBy,
  formatListeningDateTime,
  formatListeningDeviceId,
  formatListeningLocation,
  formatListeningNumberWithUnit,
  getListeningCurrentLabel,
} from "./listeningViewUtils";

const DESCRIPTION_PREVIEW_LINE_HEIGHT_EM = 1.43;
const DESCRIPTION_PREVIEW_MAX_LINES = 10;

const getAverageErrorByFrequency = (listening) =>
  listening?.averageErrorByFrequency ??
  listening?.listeningSummary?.averageErrorByFrequency ??
  {};

function MetadataRow({ listening }) {
  return (
    <Box display="flex" flexWrap="wrap" mt={1}>
      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Data: {formatListeningDateTime(listening?.createdAt)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Twórca: {formatListeningCreatedBy(listening?.createdBy)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Device ID: {formatListeningDeviceId(listening?.deviceId)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Lokalizacja: {formatListeningLocation(listening?.location)}
        </Typography>
      </Box>
    </Box>
  );
}

function ExpandableDescription({ value }) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);

  const displayValue =
    value !== undefined && value !== null && String(value) !== ""
      ? String(value)
      : "-";

  useEffect(() => {
    const element = textRef.current;
    if (!element || expanded) {
      setIsExpandable(false);
      return;
    }

    const checkOverflow = () => {
      if (!element) {
        return;
      }

      setIsExpandable(element.scrollHeight > element.clientHeight + 1);
    };

    checkOverflow();

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(checkOverflow);
    }
  }, [displayValue, expanded]);

  return (
    <Box mt={1}>
      <Typography
        ref={textRef}
        variant="body2"
        style={{
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          tabSize: 4,
          lineHeight: `${DESCRIPTION_PREVIEW_LINE_HEIGHT_EM}em`,
          overflow: "hidden",
          maxHeight: expanded
            ? "none"
            : `${
                DESCRIPTION_PREVIEW_LINE_HEIGHT_EM *
                DESCRIPTION_PREVIEW_MAX_LINES
              }em`,
        }}
      >
        {displayValue}
      </Typography>

      {!expanded && isExpandable && (
        <Button
          size="small"
          color="primary"
          onClick={() => setExpanded(true)}
          style={{
            marginTop: 4,
            padding: 0,
            minWidth: "auto",
            textTransform: "none",
          }}
        >
          Więcej...
        </Button>
      )}
    </Box>
  );
}

function AverageErrorTable({ listening }) {
  const entries = Object.entries(getAverageErrorByFrequency(listening));

  if (!entries.length) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {entries.map(([frequency]) => (
              <TableCell
                key={`head-${listening?.id}-${frequency}`}
                align="center"
              >
                {frequency} Hz
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell>Uśredniony błąd</TableCell>
            {entries.map(([frequency, value]) => (
              <TableCell
                key={`value-${listening?.id}-${frequency}`}
                align="center"
              >
                {formatListeningNumberWithUnit(value, "dB")}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ListeningListView({
  listenings,
  loading,
  error,
  isByModel,
  onOpenDetails,
  onDeleteListening,
}) {
  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!listenings || listenings.length === 0) {
    return (
      <Typography color="textSecondary">
        Brak odsłuchów dla tej kalibracji.
      </Typography>
    );
  }

  return (
    <Box>
      {listenings.map((listening, index) => {
        const statusLabel = getListeningCurrentLabel(listening?.current);

        return (
          <Paper
            key={listening?.id || `listening-${index}`}
            variant="outlined"
            style={{ padding: 16, marginBottom: 16 }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
            >
              <Box mr={2} mb={2} style={{ minWidth: 280, flex: 1 }}>
                <Typography variant="h6">
                  {listening?.title || "Bez tytułu"}
                </Typography>

                <MetadataRow listening={listening} />

                <ExpandableDescription value={listening?.description} />
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                mb={2}
              >
                {isByModel && statusLabel ? (
                  <Chip
                    label={statusLabel}
                    color={listening?.current ? "primary" : "default"}
                    style={{ marginBottom: 8 }}
                  />
                ) : null}

                <Typography variant="body2" color="textSecondary">
                  Średnia bezwzględna różnica
                </Typography>

                <Typography variant="subtitle2">
                  {formatListeningNumberWithUnit(
                    listening?.averageAbsoluteError ??
                      listening?.listeningSummary?.averageAbsoluteError,
                    "dB",
                  )}
                </Typography>
              </Box>
            </Box>

            <Box mb={2}>
              <AverageErrorTable listening={listening} />
            </Box>

            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                color="primary"
                startIcon={<VisibilityIcon />}
                onClick={() => onOpenDetails(listening?.id)}
                style={{ marginRight: 8 }}
              >
                Szczegóły
              </Button>

              <Button
                size="small"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={() => onDeleteListening(listening)}
              >
                Usuń
              </Button>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
